import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'
import { RequireAuth } from '@dss/auth'
import { ClientsWorkspaceScreen } from '../features/clients/ClientsWorkspaceScreen'

/** /clients — admin-список клиентов с фильтром по portal_owned_overlay
 *  (DO-8 / D18-8 §«portal_owned_overlay»). Permission-gating внутри
 *  экрана: без `distributor.clients.manage-overlay` показывается warning
 *  Banner.
 *
 *  Файл назван `clients.index.tsx`, чтобы при будущем добавлении
 *  `clients.$id.tsx` flatten-паттерн уже был на месте (см. orders.index.tsx,
 *  territories.index.tsx).
 *
 *  Audit follow-up (P2-C3): фильтры (overlay, includePending,
 *  primary_warehouse_id) и pagination сериализуются в URL search-params для
 *  shareable-ссылок и back/forward navigation. Schema через zod + .catch на
 *  невалидных значениях. */
const PAGE_SIZE_VALUES = [25, 50, 100] as const

const clientsSearchSchema = z
  .object({
    overlay: z.boolean().optional(),
    includePending: z.boolean().optional(),
    primaryWarehouseId: z.array(z.coerce.number().int()).optional(),
    page: z.coerce.number().int().min(0).optional(),
    size: z.coerce.number().int().pipe(z.union([z.literal(25), z.literal(50), z.literal(100)])).optional(),
  })
  .partial()

export type ClientsSearch = z.infer<typeof clientsSearchSchema>

export const Route = createFileRoute('/clients/')({
  validateSearch: (search): ClientsSearch => {
    // TanStack отдаёт raw record (string | string[] | undefined). Нормализуем
    // массивы и booleans перед zod-парсингом.
    const normalized: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(search as Record<string, unknown>)) {
      if (key === 'primaryWarehouseId') {
        normalized[key] = Array.isArray(value) ? value : value != null ? [value] : value
      } else if (key === 'overlay' || key === 'includePending') {
        // ?overlay=true / ?overlay=false / ?overlay (без value).
        if (value === 'true' || value === true) normalized[key] = true
        else if (value === 'false' || value === false) normalized[key] = false
        else normalized[key] = undefined
      } else {
        normalized[key] = value
      }
    }
    const parsed = clientsSearchSchema.safeParse(normalized)
    return parsed.success ? parsed.data : {}
  },
  component: ClientsRoute,
})

function ClientsRoute() {
  const search = Route.useSearch()
  return (
    <RequireAuth>
      <ClientsWorkspaceScreen initialSearch={search} />
    </RequireAuth>
  )
}

// Подсказка для AdminClientsScreen: разрешённый набор PAGE_SIZE значений.
export const CLIENTS_PAGE_SIZES = PAGE_SIZE_VALUES
